import type { NextPage } from 'next'
import { GetServerSideProps } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { isLiveMallClub } from '@/constants/systemType'

// Libs
import { fetchRoomData } from '@/api/fetchRoomData'
import { getSchemaOrg } from '@/methods/handleSchemaOrg'

// Components
import LiveRoomComponent from '@/components/pages/LiveRoom'

// Constants
import { ROUTE_PATH } from '@/constants/routePath'
import { envConfigs } from '@/constants/config'
import { DEFAULT_LOCALE } from '@/constants/defaultLocale'

// Styles

export interface LiveRoomProps {
  liveData: {
    title: string
    coverPhotoUrl: string
    startTime: string
    endTime: string
    roomId: string
    sessionRoomId: string
    tickerTape: string[]
    roomDesc: string
    roomPlacard: string
    dummyViews: number
    kolName: string
    clientSn: number
    liveType: number
    memberInfoId: string
    kolHeadshotUrl: string
    kolFirstEntry: string
    likes: number
    isRecord: boolean
    liveTime: string
    liveCloseTime: string
  }
}

// Variables

const LiveRoom: NextPage = (props: any) => {
  const { pageProps } = props
  const { liveData }: LiveRoomProps = pageProps

  const { t } = useTranslation()

  const router = useRouter()

  const currentLocale = router.locale === 'default' ? DEFAULT_LOCALE : router.locale

  const liveImage = liveData?.coverPhotoUrl || ''

  const title = isLiveMallClub
    ? 'Live Mall Club'
    : t('Meta_Title_LiveRoom', { kolName: liveData?.kolName || 'TutorABC Shop', brandName: 'TutorABC Shop' })
  const imageUrl = liveImage ? `${envConfigs.domainUrl}/api/og/room?image=${liveImage}` : `/images/og-default.png`
  const ogUrl = `${envConfigs.domainUrl}/${currentLocale}${router.asPath}`
  const description = isLiveMallClub ? 'Live Mall Club' : liveData?.title || 'TutorABC Shop'

  const schema = getSchemaOrg({
    type: 'WebSite',
    name: title,
    image: imageUrl,
    description,
    url: ogUrl,
    breadCrumbList: [
      {
        name: t('Meta_Title_Live', { brandName: 'TutorABC Shop' }),
        position: 1,
        url: `${envConfigs.domainUrl}/${currentLocale}${ROUTE_PATH.LIVE}`,
      },
      {
        name: t('Search_Search'),
        position: 2,
        url: `${envConfigs.domainUrl}/${currentLocale}${ROUTE_PATH.SEARCH}?keyword=${liveData?.kolName}`,
      },
    ],
  })

  return (
    <>
      <Head>
        <title>{title}</title>
        <link rel='canonical' href={`${envConfigs.domainUrl}/${currentLocale}${ROUTE_PATH.LIVE}/${liveData?.roomId}`} />
        <meta name='title' key='meta-title' content={title} />
        <meta name='description' key='meta-description' content={description} />
        <meta property='og:title' key='og-title' content={title} />
        <meta property='og:description' key='description' content={description} />
        <meta property='og:type' key='type' content='website' />
        <meta property='og:url' key='og:url' content={ogUrl} />
        <meta property='og:image' key='og:image' content={imageUrl} />

        {!isLiveMallClub && <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />}
      </Head>
      <LiveRoomComponent />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ params, locale }) => {
  const { roomId }: any = params

  const result = await fetchRoomData({ roomId })
    .then((result: any) => result)
    .catch((error: any) => error)

  return {
    props: { liveData: result.roomId ? result : [], ...(await serverSideTranslations(locale as string, ['common'])) },
  }
}

export default LiveRoom
